import { useState, useEffect, lazy, Suspense } from 'react';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { DataProvider } from './contexts/DataContext';
import LoginForm from './components/LoginForm';
import Layout from './components/Layout';
import GenericCrud from './components/GenericCrud';
import { useData } from './contexts/DataContext';
import OnboardingModal from './components/OnboardingModal';
import { Tag } from 'lucide-react';

// Lazy load de componentes grandes
const Dashboard = lazy(() => import('./components/Dashboard'));
const TendersList = lazy(() => import('./components/TendersList'));
const ApplicationsKanban = lazy(() => import('./components/ApplicationsKanban'));
const OrganizationsList = lazy(() => import('./components/OrganizationsList'));
const ProductsList = lazy(() => import('./components/ProductsList'));
const CurrencyRatesList = lazy(() => import('./components/CurrencyRatesList'));
const EmployeesList = lazy(() => import('./components/EmployeesList'));
const CompaniesList = lazy(() => import('./components/CompaniesList'));
const GlobalEmployeesList = lazy(() => import('./components/GlobalEmployeesList'));
const PlansList = lazy(() => import('./components/PlansList'));
const TenderCalendar = lazy(() => import('./components/TenderCalendar'));
const AccountSettings = lazy(() => import('./components/AccountSettings'));
const CurrenciesTable = lazy(() => import('./components/CurrenciesTable'));

// Componente de loading para los lazy-loaded components
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Cargando...</p>
    </div>
  </div>
);

const TenderTypesCrud = () => {
  const { tenderTypes, addTenderType, updateTenderType, deleteTenderType } = useData();
  
  return (
    <GenericCrud
      title="Tipos de Licitación"
      subtitle="Gestiona los tipos de licitaciones disponibles"
      items={tenderTypes}
      onAdd={addTenderType}
      onUpdate={updateTenderType}
      onDelete={deleteTenderType}
      fields={[
        { key: 'name', label: 'Nombre', type: 'text', required: true, placeholder: 'Ej: Construcción de obra pública' },
        { key: 'description', label: 'Descripción', type: 'textarea', placeholder: 'Descripción del tipo de licitación' },
        { 
          key: 'category', 
          label: 'Categoría', 
          type: 'select', 
          required: true,
          options: [
            { value: 'goods', label: 'Bienes' },
            { value: 'services', label: 'Servicios' },
            { value: 'works', label: 'Obras' },
            { value: 'consultancy', label: 'Consultoría' }
          ]
        }
      ]}
      displayFields={[
        { key: 'category', label: 'Categoría', type: 'badge' }
      ]}
    />
  );
};

const TenderStatusesCrud = () => {
  const { tenderStatuses, addTenderStatus, updateTenderStatus, deleteTenderStatus } = useData();
  const { currentCompany } = useTenant();
  
  // Solo Licitatrak puede gestionar estados globales
  const isSuperAdmin = currentCompany?.rut === '99.999.999-9';
  
  if (!isSuperAdmin) {
    return (
      <div className="text-center py-12">
        <Tag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Acceso Restringido</h3>
        <p className="text-gray-600">
          Los estados de licitación son globales y solo pueden ser gestionados por Licitatrak.
          Todos los tenants comparten los mismos estados de licitación.
        </p>
      </div>
    );
  }
  
  return (
    <GenericCrud
      title="Estados de Licitación (Global)"
      subtitle="Gestiona los estados globales del ciclo de vida de las licitaciones para todos los tenants"
      items={tenderStatuses}
      onAdd={addTenderStatus}
      onUpdate={updateTenderStatus}
      onDelete={deleteTenderStatus}
      fields={[
        { key: 'name', label: 'Nombre', type: 'text', required: true, placeholder: 'Ej: Publicada' },
        { key: 'description', label: 'Descripción', type: 'textarea', placeholder: 'Descripción del estado' },
        { key: 'color', label: 'Color', type: 'color', required: true }
      ]}
      displayFields={[
        { key: 'color', label: 'Color', type: 'color' }
      ]}
    />
  );
};

const ApplicationStatusesCrud = () => {
  const { applicationStatuses, addApplicationStatus, updateApplicationStatus, deleteApplicationStatus } = useData();
  const { currentCompany } = useTenant();
  
  // Solo Licitatrak puede gestionar estados globales
  const isSuperAdmin = currentCompany?.rut === '99.999.999-9';
  
  if (!isSuperAdmin) {
    return (
      <div className="text-center py-12">
        <Tag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Acceso Restringido</h3>
        <p className="text-gray-600">
          Los estados de postulación son globales y solo pueden ser gestionados por Licitatrak.
          Todos los tenants comparten los mismos estados de postulación.
        </p>
      </div>
    );
  }
  
  return (
    <GenericCrud
      title="Estados de Postulación (Global)"
      subtitle="Gestiona los estados globales del flujo de postulaciones para todos los tenants"
      items={applicationStatuses}
      onAdd={addApplicationStatus}
      onUpdate={updateApplicationStatus}
      onDelete={deleteApplicationStatus}
      fields={[
        { key: 'name', label: 'Nombre', type: 'text', required: true, placeholder: 'Ej: En Análisis' },
        { key: 'description', label: 'Descripción', type: 'textarea', placeholder: 'Descripción del estado' },
        { key: 'color', label: 'Color', type: 'color', required: true },
        { key: 'order_index', label: 'Orden', type: 'number', required: true, placeholder: '1' }
      ]}
      displayFields={[
        { key: 'color', label: 'Color', type: 'color' },
        { key: 'order_index', label: 'Orden' }
      ]}
    />
  );
};

const AppContent = () => {
  const { currentCompany, currentUser, currentCompanyUser, isLoading } = useTenant();
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Verificar si debe mostrar el onboarding
  useEffect(() => {
    if (currentCompany && currentUser && !isLoading) {
      console.log('Current company user:', currentCompanyUser);
      if (currentCompanyUser && !currentCompanyUser.Onboarding1) {
        console.log('Mostrando onboarding principal');
        setShowOnboarding(true);
      }
    }
  }, [currentCompany, currentUser, currentCompanyUser, isLoading]);

  // Mostrar loading mientras se inicializa el sistema
  if (isLoading) {
    return <LoginForm />;
  }

  // Si no hay empresa o usuario después de cargar, mostrar login
  if (!currentCompany || !currentUser) {
    return <LoginForm />;
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return (
          <Suspense fallback={<PageLoader />}>
            <Dashboard />
          </Suspense>
        );
      case 'tenders':
        return (
          <Suspense fallback={<PageLoader />}>
            <TendersList />
          </Suspense>
        );
      case 'applications-kanban':
        return (
          <Suspense fallback={<PageLoader />}>
            <ApplicationsKanban />
          </Suspense>
        );
      case 'organizations':
        return (
          <Suspense fallback={<PageLoader />}>
            <OrganizationsList />
          </Suspense>
        );
      case 'tender-types':
        return <TenderTypesCrud />;
      case 'tender-statuses':
        return <TenderStatusesCrud />;
      case 'application-statuses':
        return <ApplicationStatusesCrud />;
      case 'products':
        return (
          <Suspense fallback={<PageLoader />}>
            <ProductsList />
          </Suspense>
        );
      case 'currency-rates':
        return (
          <Suspense fallback={<PageLoader />}>
            <CurrencyRatesList />
          </Suspense>
        );
      case 'currencies':
        return (
          <Suspense fallback={<PageLoader />}>
            <CurrenciesTable />
          </Suspense>
        );
      case 'employees':
        return (
          <Suspense fallback={<PageLoader />}>
            <EmployeesList />
          </Suspense>
        );
      case 'companies':
        return (
          <Suspense fallback={<PageLoader />}>
            <CompaniesList />
          </Suspense>
        );
      case 'global-employees':
        return (
          <Suspense fallback={<PageLoader />}>
            <GlobalEmployeesList />
          </Suspense>
        );
      case 'plans':
        return (
          <Suspense fallback={<PageLoader />}>
            <PlansList />
          </Suspense>
        );
      case 'calendar':
        return (
          <Suspense fallback={<PageLoader />}>
            <TenderCalendar onPageChange={setCurrentPage} />
          </Suspense>
        );
      case 'account':
        return (
          <Suspense fallback={<PageLoader />}>
            <AccountSettings />
          </Suspense>
        );
      default:
        return (
          <Suspense fallback={<PageLoader />}>
            <Dashboard />
          </Suspense>
        );
    }
  };

  return (
    <DataProvider>
      <Layout currentPage={currentPage} onPageChange={setCurrentPage}>
        {renderPage()}
      </Layout>
      
      {/* Modal de onboarding */}
      <OnboardingModal
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={() => setShowOnboarding(false)}
      />
    </DataProvider>
  );
};

function App() {
  return (
    <TenantProvider>
      <AppContent />
    </TenantProvider>
  );
}

export default App;